import { signProfileAsset } from "@/lib/profileAssetSigning.mjs";
// src/features/settings/SettingsHomeCommon.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import List from "@/sot/primitives/List.jsx";
import SettingsChevron from "@/sot/primitives/SettingsChevron.jsx";
import { type as T } from "@/sot/tokens/typography.js";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import {
  readAvatarSessionCache,
  writeAvatarSessionCache,
} from "@/lib/avatarSessionCache.js";
import {
  profileText,
  resolveAddressedProfileName,
} from "@/pages/shared/profileAddressedName.js";
import supabase from "@/services/supabaseClient";
import SettingsLogoutDialog from "@/features/settings/SettingsLogoutDialog.jsx";

export default function SettingsHomeCommon({ role = "patient" }) {
  const nav = useNavigate();
  const auth = (typeof useAuth === "function" ? useAuth() : {}) || {};
  const { t } = useLanguage();
  const user = auth.user || null;
  const profile = auth.profile || null;

  const base = `/settings/${role === "doctor" ? "doctor" : "patient"}`;
  const mainSettingsItems = [
    {
      leading: "/icons/list-items/encrypted.svg",
      title: t("settings.home.loginSecurity", "Account & Security"),
      trailing: <SettingsChevron />,
      onClick: () => nav(`${base}/login-security`),
    },
    {
      leading: "/icons/list-items/language_default.svg",
      title: t("settings.home.language", "Language"),
      trailing: <SettingsChevron />,
      onClick: () => nav(`${base}/language`),
    },
    ...(role === "doctor"
      ? [
          {
            leading: "/icons/list-items/schedule_Fill.svg",
            title: t("settings.home.availability", "Availability"),
            trailing: <SettingsChevron />,
            onClick: () => nav(`${base}/p/availability`),
          },
        ]
      : []),
    {
      leading: "/icons/list-items/notification1.svg",
      title: t("settings.home.notifications", "Notifications"),
      trailing: <SettingsChevron />,
      onClick: () => nav(`${base}/notifications`),
    },
    {
      leading: "/icons/list-items/settings.svg",
      title: t("settings.home.preferences", "Preferences"),
      trailing: <SettingsChevron />,
      onClick: () => nav(`${base}/preferences`),
      divider: false,
    },
  ];

  // Full name resolution (doctor or patient)
  const profileNameSource =
    profileText(profile?.full_name) ||
    [profileText(profile?.first_name), profileText(profile?.last_name)].filter(Boolean).join(" ") ||
    profileText(profile?.display_name);

  const profileFullName =
    resolveAddressedProfileName(role, profile, "") ||
    profileNameSource ||
    (user?.email ? user.email.split("@")[0] : t("settings.home.userFallback", "User"));

  const cachedAvatar = readAvatarSessionCache({ role, userId: user?.id });
  const profilePhotoPath = profile?.profile_photo_path || "";
  const profilePhotoUrl = profile?.profile_image_url || profile?.avatar_url || "";
  const [avatarResult, setAvatarResult] = React.useState(null);
  const [failedSrc, setFailedSrc] = React.useState("");
  const [logoutOpen, setLogoutOpen] = React.useState(false);
  const resolvedName = profileFullName;
  const candidate = (avatarResult?.userId === user?.id && avatarResult?.path === profilePhotoPath ? avatarResult.src : "") || cachedAvatar || profilePhotoUrl;
  const displayAvatarSrc = candidate && candidate !== failedSrc ? candidate : "/icons/Profile_Avatar.svg";

  React.useEffect(() => {
    let alive = true;
    if (!user?.id || !profilePhotoPath) return undefined;
    void signProfileAsset(supabase, `${role}-profile-images`, profilePhotoPath).then(({ data }) => {
      if (!alive || !data?.signedUrl) return;
      writeAvatarSessionCache({ role, userId: user.id, src: data.signedUrl });
      setAvatarResult({ userId: user.id, path: profilePhotoPath, src: data.signedUrl });
    });
    return () => { alive = false; };
  }, [role, user?.id, profilePhotoPath]);

  return (
    <div className="w-full space-y-4">
      {/* profile card */}
      <List
        itemMinHeight={56}
        items={[
          {
            leading: (
              <div className="flex items-center justify-center h-11 w-11">
                <div
                  className="
                    h-11 w-11
                    rounded-[8px]
                    p-[1.5px]
                    bg-[rgb(var(--clr-surface-cont-highest))]
                    ring-1 ring-[rgba(24,50,119,0.12)]
                    shadow-[0_6px_12px_-4px_rgba(29,79,215,0.06),0_3px_6px_-2px_rgba(26,27,35,0.06),0_1.5px_0px_-0.5px_rgba(26,27,35,0.06)]
                  "
                >
                  <div
                    className="
                      relative
                      h-full w-full
                      rounded-[calc(8px-1.5px)]
                      overflow-hidden
                      bg-[rgb(var(--clr-surface-container-low))]
                    "
                  >
                    <img
                      data-afrimed-settings-avatar
                      src={displayAvatarSrc}
                      alt={resolvedName}
                      className="h-full w-full object-cover opacity-100"
                      onError={() => setFailedSrc(displayAvatarSrc)}
                      draggable="false"
                    />
                  </div>
                </div>
              </div>
            ),
            title: resolvedName,
            titleClassName: "text-title-large",
            titleStyleOverride: {
              fontSize: `${T.titleLarge.size}px`,
              lineHeight: `${T.titleLarge.line}px`,
              fontWeight: T.titleLarge.weight,
              letterSpacing: "0px",
            },
            support: t(
              "settings.home.profileSupport",
              "Edit Pages, Profile and much more...",
            ),
            trailing: <SettingsChevron />,
            divider: false,
            onClick: () => nav(`/settings/${role === "doctor" ? "doctor" : "patient"}/p`),
          },
        ]}
      />

      {/* main settings */}
      <List itemMinHeight={56} items={mainSettingsItems} />

      {/* legal */}
      <div className="px-2 text-label-medium text-on-surface-variant pt-2">
        {t("settings.home.legal", "Legal")}
      </div>

      <List
        itemMinHeight={56}
        items={[
          {
            leading: "/icons/list-items/book_5.svg",
            title: t("common.legal.terms", "Terms & Conditions"),
            trailing: <SettingsChevron />,
            onClick: () => nav(`/legal/terms`),
          },
          {
            leading: "/icons/list-items/book_5.svg",
            title: t("common.legal.privacy", "Privacy Policy"),
            trailing: <SettingsChevron />,
            onClick: () => nav(`/legal/privacy`),
          },
          {
            leading: "/icons/list-items/book_5.svg",
            title: t("settings.home.faq", "Help Centre"),
            trailing: <SettingsChevron />,
            divider: false,
            onClick: () => nav(`/legal/faq`),
          },
        ]}
      />

      {/* logout */}
      <div className="pt-4 flex justify-center">
        <button
          type="button"
          className="btn-ghost w-full max-w-[720px] text-label-large"
          onClick={() => setLogoutOpen(true)}
        >
          {t("common.logout", "Log out")}
        </button>
      </div>

      <SettingsLogoutDialog open={logoutOpen} onClose={() => setLogoutOpen(false)} />
    </div>
  );
}
